import { formatAppCurrency } from '@/renderer/lib/currency';
import { SalesReportOrderView } from '../../types/report';

// -- DATE FORMATTERS -- //
const DATE_LABEL_FORMATTER = new Intl.DateTimeFormat('en-UG', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const DATE_TIME_FORMATTER = new Intl.DateTimeFormat('en-UG', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export const PAYMENT_COLORS = [
  '#15803d',
  '#1f2937',
  '#64748b',
  '#cbd5e1',
  '#f1f5f9',
];

export const formatMoney = (value: number): string =>
  formatAppCurrency(Math.round(value));

export const formatTooltipMoney = (value: unknown): string =>
  formatMoney(typeof value === 'number' ? value : Number(value || 0));

export const parseDate = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const formatDate = (value: Date): string => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const addDays = (value: Date, days: number): Date =>
  new Date(value.getFullYear(), value.getMonth(), value.getDate() + days);

export const formatShortDate = (value: string): string =>
  DATE_LABEL_FORMATTER.format(parseDate(value));

export const formatDateTime = (value: string): string => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return DATE_TIME_FORMATTER.format(parsed);
};

export const getRangeStart = (endDate: string, days: number): string =>
  formatDate(addDays(parseDate(endDate), -(days - 1)));

export const getLatestBusinessDate = (
  selectedOutletDayOpen: string | null | undefined,
  outletDays: Array<string | null | undefined>,
): string => {
  if (selectedOutletDayOpen) return selectedOutletDayOpen;
  const latest = outletDays.filter(Boolean).sort().at(-1);
  if (latest) return latest;
  return formatDate(new Date());
};

export const getUserName = (
  firstName?: string | null,
  lastName?: string | null,
) => {
  const parts = [firstName, lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : 'System user';
};

export const getOrderLabel = (order: SalesReportOrderView) => {
  if (order.table_name) {
    return order.bill_number
      ? `${order.table_name} #${order.bill_number}`
      : order.table_name;
  }
  if (order.client_name) return order.client_name;
  return order.mode;
};

export const getStatusBadgeClass = (status: string) => {
  const normalized = status.toLowerCase();
  if (normalized.includes('settled')) return 'bg-emerald-50 text-emerald-700';
  if (normalized.includes('open') || normalized.includes('pending'))
    return 'bg-amber-50 text-amber-700';
  if (normalized.includes('cancel')) return 'bg-rose-50 text-rose-700';
  return 'bg-slate-100 text-slate-700';
};
